<script setup>
// Conversa que veio de um comentário (D46 e D47, variação "Respondeu no
// Direct" do mockup sugestao-no-chat.html): no topo do chat, o post de
// origem, o comentário da pessoa, a resposta pública que saiu e o selo
// "Direct enviado". Só nas caixas de Instagram e Facebook, com o módulo de
// Comentários ligado, e só quando o comentário já saiu da fila (o pendente
// aparece em balões no fim do chat, AiCenterOpportunitySuggestion.vue).
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import AiCenterAPI from 'dashboard/api/aiCenter';
import { isAiCenterModuleEnabled } from 'dashboard/routes/dashboard/aiCenter/aiCenterModules';

const props = defineProps({
  conversationId: { type: Number, required: true },
  channel: { type: String, default: '' },
});

const SOCIAL_CHANNELS = ['Channel::Instagram', 'Channel::FacebookPage'];
const DONE_STATUSES = ['sent', 'completed', 'received'];

const { t } = useI18n();
const route = useRoute();
const { isFeatureFlagEnabled } = usePolicy();
const item = ref(null);
let token = 0;

const enabled = computed(
  () =>
    SOCIAL_CHANNELS.includes(props.channel) &&
    isAiCenterModuleEnabled('social', isFeatureFlagEnabled)
);

const received = (source, destination) =>
  (source?.deliveries || []).find(
    delivery =>
      delivery.destination === destination && delivery.status === 'received'
  );
const publicReply = computed(() => received(item.value, 'comment'));
const directSent = computed(() => Boolean(received(item.value, 'direct')));

const load = async () => {
  token += 1;
  const current = token;
  item.value = null;
  if (!enabled.value || !props.conversationId) return;
  try {
    const response = await AiCenterAPI.getItems(route.params.accountId, {
      tab: 'social',
      conversation_display_id: props.conversationId,
      per_page: 5,
    });
    if (current !== token) return;
    item.value =
      (response.data?.data || []).find(
        candidate =>
          DONE_STATUSES.includes(candidate.status) ||
          received(candidate, 'direct') ||
          received(candidate, 'comment')
      ) || null;
  } catch {
    // A origem é complementar: sem ela, a conversa segue normal.
  }
};

const onItemUpdated = data => {
  if (
    data?.kind === 'social' &&
    Number(data.conversation_id) === props.conversationId
  ) {
    load();
  }
};

onMounted(() => emitter.on(BUS_EVENTS.AI_CENTER_ITEM_UPDATED, onItemUpdated));
onBeforeUnmount(() =>
  emitter.off(BUS_EVENTS.AI_CENTER_ITEM_UPDATED, onItemUpdated)
);
watch(() => [props.conversationId, enabled.value], load, { immediate: true });
</script>

<template>
  <li
    v-if="item"
    class="mb-3 flex list-none flex-col gap-2"
    data-testid="ai-center-comment-origin"
  >
    <span
      class="inline-flex items-center gap-1 self-center rounded-lg border border-n-slate-7 px-2.5 py-0.5 text-[12.5px] text-n-slate-11"
    >
      <span class="i-ph-chat-circle-text size-3.5" aria-hidden="true" />
      {{ t('AI_CENTER.CHAT_SUGGESTION.FROM_COMMENT') }}
    </span>
    <a
      v-if="item.post"
      :href="item.post.permalink || undefined"
      target="_blank"
      rel="noopener noreferrer"
      class="flex gap-2.5 rounded-[14px] border border-n-weak bg-n-solid-2 p-2.5 text-n-slate-12 no-underline"
    >
      <img
        v-if="item.post.thumbnail_url"
        :src="item.post.thumbnail_url"
        alt=""
        class="size-16 flex-none rounded-[10px] object-cover"
      />
      <span
        v-else
        class="grid size-16 flex-none place-items-center rounded-[10px] bg-n-slate-4 text-n-slate-11"
      >
        <span class="i-ph-play-fill size-[22px]" aria-hidden="true" />
      </span>
      <span class="min-w-0">
        <b class="block text-[13.5px]">
          {{ t('AI_CENTER.CHAT_SUGGESTION.POST') }}
        </b>
        <span class="mt-0.5 line-clamp-2 text-[13px] text-n-slate-11">
          {{ item.post.caption }}
        </span>
      </span>
    </a>
    <div
      v-if="item.payload?.comment_text"
      class="flex max-w-[72%] flex-col gap-0.5 self-start"
    >
      <p
        class="m-0 whitespace-pre-line break-words rounded-[18px] rounded-bl-[5px] bg-n-slate-3 px-3 py-2 text-[14.5px] text-n-slate-12"
      >
        {{ item.payload.comment_text }}
      </p>
      <small class="text-[11.5px] text-n-slate-11">
        {{ t('AI_CENTER.CHAT_SUGGESTION.COMMENT_META') }}
      </small>
    </div>
    <div
      v-if="publicReply"
      class="flex max-w-[72%] flex-col items-end gap-0.5 self-end"
    >
      <p
        class="m-0 whitespace-pre-line break-words rounded-[18px] rounded-br-[5px] bg-n-blue-9 px-3 py-2 text-[14.5px] text-white"
      >
        {{ publicReply.content }}
      </p>
      <small class="text-[11.5px] text-n-slate-11">
        {{ t('AI_CENTER.CHAT_SUGGESTION.PUBLIC_REPLY') }}
      </small>
    </div>
    <span
      v-if="directSent"
      class="inline-flex items-center gap-1.5 self-end text-xs font-bold text-n-teal-11"
      data-testid="ai-center-comment-origin-direct"
    >
      <span class="i-ph-check-bold size-3.5" aria-hidden="true" />
      {{ t('AI_CENTER.CHAT_SUGGESTION.DIRECT_SENT') }}
    </span>
  </li>
</template>

<style scoped>
a.border,
span.border {
  border-style: solid;
}
</style>
